import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { Box, TransparentButton } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import Icon from '~ComponentsImproved/Icon';
import CheckIcon from '~Assets/icons/check.svg';
import type { SearchSelectOption } from '.';

const OptionListItem = styled(TransparentButton)<{ isSelected?: boolean; isDisabled?: boolean }>`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 10px 16px;
  text-transform: none;
  text-align: left;
  min-height: 44px;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.4 : 1)};
  border: ${({ isSelected }) =>
    isSelected ? '1px solid var(--colorBlueGray700)' : '1px solid var(--colorBlueGray1100)'};
`;

/** Shared so every row reuses the same style objects instead of allocating new ones */
const ITALIC_STYLE = { fontStyle: 'italic' } as const;
const NORMAL_STYLE = { fontStyle: 'normal' } as const;

interface Props {
  option: SearchSelectOption;
  isSelected: boolean;
  isHandy: boolean;
  /** Stable callback from SearchSelect so memoisation holds */
  onSelect: (id: string) => void;
}

/** One selectable row; memoised so typing in the search only re-renders rows that changed */
const OptionRow: FC<Props> = ({ option, isSelected, isHandy, onSelect }) => (
  <OptionListItem
    display="flex"
    justifyContent="space-between"
    fluid
    isSelected={isSelected}
    isDisabled={option.disabled}
    onClick={() => !option.disabled && onSelect(option.id)}>
    <Box display="flex" flexDirection="row" gap="small" alignItems="center">
      {option.icon && (
        <Icon src={option.icon} height="24px" width="24px" color="var(--colorBlueGray200)" />
      )}
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        <Heading
          level={4}
          bottom={0}
          color="var(--colorWhite)"
          style={option.italic ? ITALIC_STYLE : NORMAL_STYLE}>
          {option.label}
        </Heading>
        {option.description && (
          <Paragraph level={5} bottom={0} color="var(--colorBlueGray300)">
            {option.description}
          </Paragraph>
        )}
      </Box>
    </Box>
    {(option.detail || option.secondaryDetail) && (
      <Box display="flex" alignItems="center" gap="xs">
        {option.detail && (
          <Paragraph level={isHandy ? 5 : 4} bottom={0} color="var(--colorWhite)">
            {option.detail}
          </Paragraph>
        )}
        {option.secondaryDetail && (
          <Paragraph level={isHandy ? 5 : 4} bottom={0} color="var(--colorBlueGray300)">
            {option.secondaryDetail}
          </Paragraph>
        )}
      </Box>
    )}
    <Icon
      src={CheckIcon}
      height="20px"
      width="20px"
      color={isSelected ? 'var(--colorWhite)' : 'var(--colorBlueGray1100)'}
    />
  </OptionListItem>
);

export default memo(OptionRow);
